/**
 * CustomTargetsEditor Component
 * 
 * Allows users to set custom calorie and macro targets with optional preset prefill.
 * Features:
 * - Toggle between MIQ Recommended and Custom Targets
 * - Optional preset dropdown (Lean Bulk, Clean Cut, Balanced, Keto-Style)
 * - Manual input fields for Calories, Protein, Carbs, Fat
 * - Calorie-macro consistency validation
 * - Save Custom Targets / Reset to MIQ buttons
 */

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, Check, ChevronDown, Info } from 'lucide-react';

// Backend URL from environment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Preset configurations (must match backend)
const PRESETS = {
  lean_bulk: {
    id: 'lean_bulk',
    name: 'Lean Bulk (High Protein)',
    description: 'High protein, moderate carbs for muscle gain with minimal fat',
    emoji: '💪'
  },
  clean_cut: {
    id: 'clean_cut',
    name: 'Clean Cut (High Protein / Lower Carb)',
    description: 'High protein, lower carbs for fat loss while preserving muscle',
    emoji: '🔥'
  },
  balanced: {
    id: 'balanced',
    name: 'Balanced',
    description: 'Even distribution of macros for maintenance',
    emoji: '⚖️'
  },
  keto_style: {
    id: 'keto_style',
    name: 'Keto-Style (Low Carb / Higher Fat)',
    description: 'Very low carb, high fat for ketogenic approach',
    emoji: '🥑'
  }
};

const CustomTargetsEditor = ({
  userId,
  currentTargetsMode = 'miq',
  currentCustomTargets = null,
  miqRecommended = null,
  onTargetsUpdated,
  onClose
}) => {
  // State
  const [targetsMode, setTargetsMode] = useState(currentTargetsMode);
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fat, setFat] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('');
  const [showPresetDropdown, setShowPresetDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showConsistencyWarning, setShowConsistencyWarning] = useState(false);
  const [consistencyData, setConsistencyData] = useState(null);

  // Initialize values from props
  useEffect(() => {
    if (targetsMode === 'custom' && currentCustomTargets) {
      setCalories(currentCustomTargets.calories?.toString() || '');
      setProtein(currentCustomTargets.protein_g?.toString() || '');
      setCarbs(currentCustomTargets.carbs_g?.toString() || '');
      setFat(currentCustomTargets.fat_g?.toString() || '');
    } else if (miqRecommended) {
      setCalories(miqRecommended.calories?.toString() || '');
      setProtein(miqRecommended.protein_g?.toString() || '');
      setCarbs(miqRecommended.carbs_g?.toString() || '');
      setFat(miqRecommended.fat_g?.toString() || '');
    }
  }, [targetsMode, currentCustomTargets, miqRecommended]);

  // Calculate calories from macros
  const calculateCaloriesFromMacros = useCallback(() => {
    const p = parseInt(protein) || 0;
    const c = parseInt(carbs) || 0;
    const f = parseInt(fat) || 0;
    return (p * 4) + (c * 4) + (f * 9);
  }, [protein, carbs, fat]);

  // Check calorie-macro consistency
  const checkConsistency = useCallback(() => {
    const inputCalories = parseInt(calories) || 0;
    const calculatedCalories = calculateCaloriesFromMacros();
    
    if (inputCalories <= 0) return { consistent: true, deviation: 0 };
    
    const deviation = Math.abs(calculatedCalories - inputCalories) / inputCalories;
    return {
      consistent: deviation <= 0.10,
      deviation: Math.round(deviation * 100),
      calculated: calculatedCalories,
      input: inputCalories
    };
  }, [calories, calculateCaloriesFromMacros]);

  // Load preset values
  const loadPresetValues = async (presetId) => {
    if (!presetId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-nutrition-goals/${userId}/preset-values/${presetId}`);
      if (response.ok) {
        const data = await response.json();
        const values = data.prefill_values;
        setCalories(values.calories.toString());
        setProtein(values.protein_g.toString());
        setCarbs(values.carbs_g.toString());
        setFat(values.fat_g.toString());
        setSelectedPreset(presetId);
        setSuccessMessage(`${PRESETS[presetId]?.emoji || '✓'} Preset applied — customize freely!`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error('Failed to load preset');
      }
    } catch (err) {
      console.error('Error loading preset:', err);
      setError('Failed to load preset. Please try again.');
    } finally {
      setLoading(false);
      setShowPresetDropdown(false);
    }
  };

  // Save custom targets
  const saveCustomTargets = async (forceConsistency = false) => {
    // Validate
    const cal = parseInt(calories);
    const pro = parseInt(protein);
    const carb = parseInt(carbs);
    const fatVal = parseInt(fat);
    
    if (!cal || cal <= 0) {
      setError('Calories must be greater than 0');
      return;
    }
    if (pro < 0 || carb < 0 || fatVal < 0) {
      setError('Macros cannot be negative');
      return;
    }
    
    // Check consistency
    const consistency = checkConsistency();
    if (!consistency.consistent && !forceConsistency) {
      setConsistencyData(consistency);
      setShowConsistencyWarning(true);
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-nutrition-goals/${userId}/custom-targets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          calories: cal,
          protein_g: pro,
          carbs_g: carb,
          fat_g: fatVal
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setTargetsMode('custom');
        setSuccessMessage('✅ Custom targets saved!');
        setShowConsistencyWarning(false);
        if (onTargetsUpdated) {
          onTargetsUpdated(data);
        }
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save targets');
      }
    } catch (err) {
      console.error('Error saving custom targets:', err);
      setError(err.message || 'Failed to save targets. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Reset to MIQ
  const resetToMIQ = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-nutrition-goals/${userId}/reset-to-miq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTargetsMode('miq');
        setSelectedPreset('');
        
        // Reset to MIQ recommended values
        if (data.miq_recommended) {
          setCalories(data.miq_recommended.calories?.toString() || '');
          setProtein(data.miq_recommended.protein_g?.toString() || '');
          setCarbs(data.miq_recommended.carbs_g?.toString() || '');
          setFat(data.miq_recommended.fat_g?.toString() || '');
        }
        
        setSuccessMessage('🔄 Reset to MIQ Recommended!');
        if (onTargetsUpdated) {
          onTargetsUpdated(data);
        }
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error('Failed to reset targets');
      }
    } catch (err) {
      console.error('Error resetting to MIQ:', err);
      setError('Failed to reset. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle mode change
  const handleModeChange = (mode) => {
    setTargetsMode(mode);
    setSelectedPreset('');
    setError(null);
    setSuccessMessage(null);
    
    if (mode === 'miq' && miqRecommended) {
      setCalories(miqRecommended.calories?.toString() || '');
      setProtein(miqRecommended.protein_g?.toString() || '');
      setCarbs(miqRecommended.carbs_g?.toString() || '');
      setFat(miqRecommended.fat_g?.toString() || '');
    }
  };

  return (
    <div data-testid="custom-targets-editor">
      {/* Mode Toggle */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '12px', 
          fontWeight: '600', 
          color: 'var(--text-secondary)', 
          marginBottom: '8px' 
        }}>
          Target Mode
        </label>
        <div style={{ 
          display: 'flex', 
          gap: '8px',
          background: 'rgba(255,255,255,0.03)',
          padding: '4px',
          borderRadius: '10px'
        }}>
          <button
            data-testid="mode-miq"
            onClick={() => handleModeChange('miq')}
            style={{
              flex: 1,
              padding: '10px 16px',
              background: targetsMode === 'miq' 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'transparent',
              border: 'none',
              borderRadius: '8px',
              color: targetsMode === 'miq' ? 'white' : 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            🧠 MIQ Recommended
          </button>
          <button
            data-testid="mode-custom"
            onClick={() => handleModeChange('custom')}
            style={{
              flex: 1,
              padding: '10px 16px',
              background: targetsMode === 'custom'
                ? 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)'
                : 'transparent',
              border: 'none',
              borderRadius: '8px',
              color: targetsMode === 'custom' ? 'white' : 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            ✏️ Custom Targets
          </button>
        </div>
      </div>

      {/* Custom Mode Content */}
      {targetsMode === 'custom' && (
        <>
          {/* Preset Dropdown (Optional) */}
          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '11px', 
              color: 'var(--text-secondary)', 
              marginBottom: '6px' 
            }}>
              Prefill Preset (Optional)
            </label>
            <button
              onClick={() => setShowPresetDropdown(!showPresetDropdown)}
              disabled={loading}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                color: selectedPreset ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <span>
                {selectedPreset 
                  ? `${PRESETS[selectedPreset]?.emoji} ${PRESETS[selectedPreset]?.name}`
                  : 'Select a preset to prefill values...'
                }
              </span>
              <ChevronDown size={16} style={{ 
                transform: showPresetDropdown ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s'
              }} />
            </button>
            
            {showPresetDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#1a1a2e',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '8px',
                marginTop: '4px',
                zIndex: 1000,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                overflow: 'hidden'
              }}>
                {Object.values(PRESETS).map((preset, idx) => (
                  <button
                    key={preset.id}
                    onClick={() => loadPresetValues(preset.id)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: idx < Object.values(PRESETS).length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                      color: 'white',
                      fontSize: '13px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '3px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(102, 126, 234, 0.15)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontWeight: '600' }}>
                      {preset.emoji} {preset.name}
                    </span>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>
                      {preset.description}
                    </span>
                  </button>
                ))}
              </div>
            )}
            
            <p style={{ 
              margin: '6px 0 0 0', 
              fontSize: '10px', 
              color: 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <Info size={12} />
              Preset is a starting point — customize freely.
            </p>
          </div>

          {/* Input Fields */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '12px',
            marginBottom: '16px'
          }}>
            {/* Calories */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                color: 'rgba(255,255,255,0.6)', 
                marginBottom: '4px' 
              }}>
                Calories (kcal/day)
              </label>
              <input
                data-testid="input-calories"
                type="number"
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
                placeholder="e.g., 2000"
                min="0"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Protein */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                color: 'rgba(255,255,255,0.6)', 
                marginBottom: '4px' 
              }}>
                Protein (g/day)
              </label>
              <input
                data-testid="input-protein"
                type="number"
                value={protein}
                onChange={(e) => setProtein(e.target.value)}
                placeholder="e.g., 150"
                min="0"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(129, 199, 132, 0.4)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Carbs */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                color: 'rgba(255,255,255,0.6)', 
                marginBottom: '4px' 
              }}>
                Carbs (g/day)
              </label>
              <input
                data-testid="input-carbs"
                type="number"
                value={carbs}
                onChange={(e) => setCarbs(e.target.value)}
                placeholder="e.g., 200"
                min="0"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255, 213, 79, 0.4)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Fat */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                color: 'rgba(255,255,255,0.6)', 
                marginBottom: '4px' 
              }}>
                Fat (g/day)
              </label>
              <input
                data-testid="input-fat"
                type="number"
                value={fat}
                onChange={(e) => setFat(e.target.value)}
                placeholder="e.g., 65"
                min="0"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255, 138, 101, 0.4)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Calculated Summary */}
          <div style={{ 
            padding: '12px',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '12px',
            color: 'rgba(255,255,255,0.6)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Calories from macros:</span>
              <span style={{ fontWeight: '600', color: 'white' }}>
                {calculateCaloriesFromMacros()} kcal
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Your target:</span>
              <span style={{ fontWeight: '600', color: 'white' }}>
                {calories || 0} kcal
              </span>
            </div>
            {!checkConsistency().consistent && calories && (
              <div style={{ 
                marginTop: '8px',
                padding: '8px',
                background: 'rgba(245, 158, 11, 0.1)',
                borderRadius: '6px',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '11px',
                color: '#f59e0b'
              }}>
                <AlertTriangle size={14} />
                <span>Macros are {checkConsistency().deviation}% off from calories</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              data-testid="save-custom-targets"
              onClick={() => saveCustomTargets(false)}
              disabled={saving || !calories}
              style={{
                flex: 2,
                padding: '14px',
                background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
                border: 'none',
                borderRadius: '10px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: saving || !calories ? 'not-allowed' : 'pointer',
                opacity: saving || !calories ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              {saving ? (
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Check size={16} />
              )}
              {saving ? 'Saving...' : 'Save Custom Targets'}
            </button>
            
            <button
              data-testid="reset-to-miq"
              onClick={resetToMIQ}
              disabled={saving || targetsMode === 'miq'}
              style={{
                flex: 1,
                padding: '14px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '10px',
                color: 'var(--text-primary)',
                fontSize: '12px',
                fontWeight: '500',
                cursor: saving || targetsMode === 'miq' ? 'not-allowed' : 'pointer',
                opacity: saving || targetsMode === 'miq' ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <RefreshCw size={14} />
              Reset to MIQ
            </button>
          </div>
        </>
      )}

      {/* MIQ Mode - Show current targets */}
      {targetsMode === 'miq' && miqRecommended && (
        <div style={{ 
          padding: '16px',
          background: 'rgba(102, 126, 234, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(102, 126, 234, 0.2)'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '12px'
          }}>
            <span style={{ fontSize: '20px' }}>🧠</span>
            <div>
              <span style={{ 
                fontSize: '13px', 
                fontWeight: '600', 
                color: 'var(--text-primary)' 
              }}>
                MIQ Recommended Targets
              </span>
              <span style={{ 
                display: 'block',
                fontSize: '10px', 
                color: 'var(--text-secondary)' 
              }}>
                Personalized based on your profile & goals
              </span>
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Calories</span>
              <p style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {miqRecommended.calories}
              </p>
            </div>
            <div>
              <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Protein</span>
              <p style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#667eea' }}>
                {miqRecommended.protein_g}g
              </p>
            </div>
            <div>
              <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Carbs</span>
              <p style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#f59e0b' }}>
                {miqRecommended.carbs_g}g
              </p>
            </div>
            <div>
              <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Fat</span>
              <p style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#ef4444' }}>
                {miqRecommended.fat_g}g
              </p>
            </div>
          </div>
          
          <p style={{ 
            margin: '12px 0 0 0', 
            fontSize: '11px', 
            color: 'var(--text-secondary)',
            fontStyle: 'italic'
          }}>
            MIQ continuously optimizes these targets based on your progress.
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{ 
          marginTop: '12px',
          padding: '10px 12px',
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#ef4444',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div style={{ 
          marginTop: '12px',
          padding: '10px 12px',
          background: 'rgba(56, 239, 125, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(56, 239, 125, 0.3)',
          color: '#38ef7d',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Check size={16} />
          {successMessage}
        </div>
      )}

      {/* Consistency Warning Modal */}
      {showConsistencyWarning && consistencyData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-primary)',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '340px',
            width: '100%',
            textAlign: 'center'
          }}>
            <span style={{ fontSize: '40px', display: 'block', marginBottom: '12px' }}>⚠️</span>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
              Calorie-Macro Mismatch
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              Your macros calculate to <strong>{consistencyData.calculated} kcal</strong>, but you entered <strong>{consistencyData.input} kcal</strong> ({consistencyData.deviation}% difference).
            </p>
            <p style={{ margin: '0 0 20px 0', fontSize: '12px', color: 'var(--text-muted)' }}>
              This may result in inaccurate tracking. Would you like to save anyway?
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowConsistencyWarning(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Adjust Values
              </button>
              <button
                data-testid="save-anyway"
                onClick={() => {
                  setShowConsistencyWarning(false);
                  saveCustomTargets(true);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Save Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CustomTargetsEditor;
